import { useState, ChangeEvent } from 'react';
import { db } from '../db/db';
import { useStore } from '../store/useStore';
import { AppModal } from '../components/AppModal';

const getMaxCompanies = (): number => {
  const licenseInfo = useStore.getState().licenseInfo;
  if (!licenseInfo) return 1;
  if (licenseInfo.maxCompanies && licenseInfo.maxCompanies > 0) return licenseInfo.maxCompanies;
  if (licenseInfo.plan === 'unlimited') return Infinity;
  if (licenseInfo.plan === 'pro') return 5;
  if (licenseInfo.plan === 'basic') return 1;
  return 1;
};

// Clean up repeated "(Restored)" suffix
const cleanRestoreName = (name: string): string => {
  const base = name.replace(/(\s*\(Restored\))+$/i, '').trim();
  return `${base} (Restored)`;
};

// Normalize company name for matching — strips "(Restored)" suffix, lowercased
const normalizeName = (name: string): string =>
  name.replace(/(\s*\(Restored\))+$/i, '').trim().toLowerCase();

export const CompanyRestoreView = ({ handleItemSelect }: any) => {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [error, setError] = useState('');
  const [modal, setModal] = useState<{
    open: boolean;
    message: string;
    onConfirm?: () => void;
  }>({ open: false, message: '' });
  const setActiveCompany = useStore((state) => state.setActiveCompany);
  const licenseInfo       = useStore((state) => state.licenseInfo);
  const setShowLicensePanel = useStore((state) => state.setShowLicensePanel);

  // ── Trial / Demo guard ────────────────────────────────────────────────────
  if (licenseInfo?.type === 'demo' || !licenseInfo) {
    return (
      <div className="flex flex-col w-full h-full bg-[#f8faf7] z-50 absolute inset-0 font-sans text-[13px] text-black border-2 border-[#1e8585]">
        <div className="bg-[#1e8585] text-white flex justify-between px-2 py-[2px] font-bold shadow-sm">
          <span>Restore Company</span>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
          <div className="bg-white border border-[#1e8585] shadow-lg p-8 max-w-md w-full">
            <div className="text-4xl mb-4">🔒</div>
            <h2 className="text-[15px] font-bold mb-2 text-[#1e8585]">Backup &amp; Restore — Full Version Only</h2>
            <p className="text-gray-600 mb-6 text-[12px]">
              Trial mode mein Backup aur Restore feature available nahi hai.<br />
              Apna data protect karne ke liye full license activate karein.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => { setShowLicensePanel(true); handleItemSelect({ name: 'Quit' }); }}
                className="bg-[#1e8585] text-white px-6 py-2 font-bold hover:bg-[#1b7373] shadow-md"
              >
                License Activate Karein
              </button>
              <button
                onClick={() => handleItemSelect({ name: 'Quit' })}
                className="bg-gray-200 px-6 py-2 font-bold hover:bg-gray-300 shadow-md"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
        <div className="bg-[#1e8585] text-white text-[11px] px-2 py-1 flex justify-between">
          <span>Esc: Quit</span>
          <span onClick={() => handleItemSelect({ name: 'Quit' })} className="cursor-pointer hover:underline">Close</span>
        </div>
      </div>
    );
  }

  // ── Core restore logic (shared by overwrite + new-company paths) ────────────
  const doRestore = async (
    backup: any,
    overwriteCompanyId: number | null,
    isOverwrite: boolean
  ) => {
    setStatus('loading');
    try {
      const allTables = [
        db.companies, db.groups, db.ledgers, db.vouchers,
        db.voucherEntries, db.stockGroups, db.stockItems,
        db.units, db.locations, db.inventoryEntries, db.stockCategories,
        db.currencies, db.voucherTypes,
        ...((db as any).employees ? [(db as any).employees] : []),
      ];
      await db.transaction('rw', allTables, async () => {
        const oldCompany = backup.company;
        let newCompanyId: number;

        if (isOverwrite && overwriteCompanyId !== null) {
          // ── OVERWRITE: reuse same DB record, wipe its child data ────────────
          newCompanyId = overwriteCompanyId;

          // Delete child data in safe order (voucher entries before vouchers)
          const existingVouchers = await db.vouchers
            .where('companyId').equals(newCompanyId).toArray();
          const existingVoucherIds = existingVouchers.map((v: any) => v.id as number);
          if (existingVoucherIds.length > 0) {
            await db.voucherEntries.where('voucherId').anyOf(existingVoucherIds).delete();
            await db.inventoryEntries.where('voucherId').anyOf(existingVoucherIds).delete();
          }
          await db.vouchers.where('companyId').equals(newCompanyId).delete();
          await db.ledgers.where('companyId').equals(newCompanyId).delete();
          await db.groups.where('companyId').equals(newCompanyId).delete();
          await db.stockItems.where('companyId').equals(newCompanyId).delete();
          await db.stockGroups.where('companyId').equals(newCompanyId).delete();
          await db.stockCategories.where('companyId').equals(newCompanyId).delete();
          await db.units.where('companyId').equals(newCompanyId).delete();
          await db.locations.where('companyId').equals(newCompanyId).delete();
          await db.currencies.where('companyId').equals(newCompanyId).delete();
          await db.voucherTypes.where('companyId').equals(newCompanyId).delete();
          if ((db as any).employees) {
            await (db as any).employees.where('companyId').equals(newCompanyId).delete();
          }

          await db.companies.update(newCompanyId, {
            name: oldCompany.name.replace(/(\s*\(Restored\))+$/i, '').trim(),
            financialYearStart: oldCompany.financialYearStart,
            booksBeginFrom: oldCompany.booksBeginFrom,
            address: oldCompany.address || '',
            gstin: oldCompany.gstin || '',
            state: oldCompany.state || '',
            businessNature: oldCompany.businessNature || 'general',
            pincode: oldCompany.pincode || '',
            mobile: oldCompany.mobile || '',
            email: oldCompany.email || '',
            isActive: 0,
          });
        } else {
          // ── NEW COMPANY: create fresh record with "(Restored)" suffix ───────
          const restoredName = cleanRestoreName(oldCompany.name);
          newCompanyId = await db.companies.add({
            name: restoredName,
            financialYearStart: oldCompany.financialYearStart,
            booksBeginFrom: oldCompany.booksBeginFrom,
            address: oldCompany.address || '',
            gstin: oldCompany.gstin || '',
            state: oldCompany.state || '',
            businessNature: oldCompany.businessNature || 'general',
            pincode: oldCompany.pincode || '',
            mobile: oldCompany.mobile || '',
            email: oldCompany.email || '',
            isActive: 0,
          });
        }

        // ── Re-insert all child data with remapped IDs ──────────────────────
        const groupIdMap = new Map<number, number>();
        const ledgerIdMap = new Map<number, number>();
        const voucherIdMap = new Map<number, number>();
        const stockGroupIdMap = new Map<number, number>();
        const stockCategoryIdMap = new Map<number, number>();
        const stockItemIdMap = new Map<number, number>();
        const unitIdMap = new Map<number, number>();
        const locationIdMap = new Map<number, number>();

        for (const raw of (backup.units || [])) {
          const u = { ...raw }; const oldId = u.id; delete u.id; u.companyId = newCompanyId;
          unitIdMap.set(oldId, await db.units.add(u));
        }
        for (const raw of (backup.locations || [])) {
          const loc = { ...raw }; const oldId = loc.id; delete loc.id; loc.companyId = newCompanyId;
          locationIdMap.set(oldId, await db.locations.add(loc));
        }
        for (const raw of (backup.stockCategories || [])) {
          const sc = { ...raw }; const oldId = sc.id; delete sc.id; sc.companyId = newCompanyId;
          stockCategoryIdMap.set(oldId, await db.stockCategories.add(sc));
        }
        for (const raw of (backup.stockGroups || [])) {
          const sg = { ...raw }; const oldId = sg.id; delete sg.id; sg.companyId = newCompanyId;
          stockGroupIdMap.set(oldId, await db.stockGroups.add(sg));
        }

        const groups = [...(backup.groups || [])].sort((a: any, b: any) => (a.parentGroupId === null ? -1 : 1));
        for (const raw of groups) {
          const g = { ...raw }; const oldId = g.id; delete g.id; g.companyId = newCompanyId;
          if (g.parentGroupId) g.parentGroupId = groupIdMap.get(g.parentGroupId) || null;
          groupIdMap.set(oldId, await db.groups.add(g));
        }

        for (const raw of (backup.ledgers || [])) {
          const l = { ...raw }; const oldId = l.id; delete l.id; l.companyId = newCompanyId;
          if (l.groupId) l.groupId = groupIdMap.get(l.groupId) || null;
          ledgerIdMap.set(oldId, await db.ledgers.add(l));
        }

        for (const raw of (backup.stockItems || [])) {
          const si = { ...raw }; const oldId = si.id; delete si.id; si.companyId = newCompanyId;
          if (si.stockGroupId) si.stockGroupId = stockGroupIdMap.get(si.stockGroupId) || null;
          if (si.stockCategoryId) si.stockCategoryId = stockCategoryIdMap.get(si.stockCategoryId) || null;
          if (si.unitId) si.unitId = unitIdMap.get(si.unitId) || null;
          stockItemIdMap.set(oldId, await db.stockItems.add(si));
        }

        for (const raw of (backup.vouchers || [])) {
          const v = { ...raw }; const oldId = v.id; delete v.id; v.companyId = newCompanyId;
          voucherIdMap.set(oldId, await db.vouchers.add(v));
        }

        for (const raw of (backup.voucherEntries || [])) {
          const ve = { ...raw }; delete ve.id;
          ve.voucherId = voucherIdMap.get(ve.voucherId);
          ve.ledgerId = ledgerIdMap.get(ve.ledgerId);
          if (ve.voucherId && ve.ledgerId) await db.voucherEntries.add(ve);
        }

        for (const raw of (backup.inventoryEntries || [])) {
          const ie = { ...raw }; delete ie.id;
          ie.voucherId = voucherIdMap.get(ie.voucherId);
          ie.stockItemId = stockItemIdMap.get(ie.stockItemId);
          if (ie.locationId) ie.locationId = locationIdMap.get(ie.locationId) || null;
          if (ie.voucherId && ie.stockItemId) await db.inventoryEntries.add(ie);
        }

        for (const raw of (backup.currencies || [])) {
          const c = { ...raw }; delete c.id; c.companyId = newCompanyId;
          await db.currencies.add(c);
        }
        for (const raw of (backup.voucherTypes || [])) {
          const vt = { ...raw }; delete vt.id; vt.companyId = newCompanyId;
          await db.voucherTypes.add(vt);
        }
        if ((db as any).employees) {
          for (const raw of (backup.employees || [])) {
            const emp = { ...raw }; delete emp.id; emp.companyId = newCompanyId;
            await (db as any).employees.add(emp);
          }
        }
      });

      setStatus('success');
      setTimeout(() => handleItemSelect({ name: 'Quit' }), 2000);
    } catch (err: any) {
      setStatus('error');
      setError(err.message);
    }
  };

  // ── File selection handler ──────────────────────────────────────────────────
  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Parse backup first — we need company name before any limit check
    let backup: any;
    try {
      const text = await file.text();
      backup = JSON.parse(text);
    } catch {
      setStatus('error');
      setError('Could not read file. Make sure it is a valid Aarthika backup (.json).');
      e.target.value = '';
      return;
    }

    if (!backup.company?.name) {
      setStatus('error');
      setError('Invalid backup file format.');
      e.target.value = '';
      return;
    }

    e.target.value = ''; // reset input regardless of path below

    const allCompanies = await db.companies.toArray();

    // ── KEY SECURITY CHECK ────────────────────────────────────────────────────
    // Does a company with the same base name already exist?
    // If yes → overwrite in-place (same DB record, no new company created).
    // This prevents the backup→restore loop from bypassing the company limit.
    const backupBaseName = normalizeName(backup.company.name);
    const existingMatch = allCompanies.find(
      (c: any) => normalizeName(c.name) === backupBaseName
    );

    if (existingMatch) {
      // Overwrite path — no limit check needed, company count stays the same
      setModal({
        open: true,
        message: `"${existingMatch.name}" already exists. Restoring will replace all its current data with the backup. This cannot be undone. Continue?`,
        onConfirm: () => {
          setModal({ open: false, message: '' });
          doRestore(backup, existingMatch.id as number, true);
        },
      });
      return;
    }

    // ── New company path — enforce plan limit ─────────────────────────────────
    const maxCompanies = getMaxCompanies();
    if (allCompanies.length >= maxCompanies) {
      const licenseInfo = useStore.getState().licenseInfo;
      setModal({
        open: true,
        message: `Company limit reached for your ${licenseInfo?.plan || 'current'} plan (Max: ${maxCompanies}). Upgrade your plan to restore additional companies.`,
      });
      return;
    }

    // All clear — create new company
    doRestore(backup, null, false);
  };

  return (
    <div className="flex flex-col w-full h-full bg-[#f8faf7] z-50 absolute inset-0 font-sans text-[13px] text-black border-2 border-[#1e8585]">
      <AppModal
        open={modal.open}
        type="warning"
        message={modal.message}
        onConfirm={modal.onConfirm ?? (() => setModal({ open: false, message: '' }))}
      />

      <div className="bg-[#1e8585] text-white flex justify-between px-2 py-[2px] font-bold shadow-sm">
        <span>Restore Company</span>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
        <div className="bg-white p-8 border border-gray-300 shadow-lg max-w-md w-full">
          <h2 className="text-lg font-bold mb-4">Import Company Data</h2>

          {status === 'idle' && (
            <>
              <p className="mb-6 text-gray-600">Select a backup file (.json) to restore a company.</p>
              <input
                type="file"
                accept=".json"
                onChange={handleFileChange}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#1e8585] file:text-white hover:file:bg-[#1b7373] mb-6"
              />
              <button onClick={() => handleItemSelect({ name: 'Quit' })} className="bg-gray-200 px-8 py-2 font-bold hover:bg-gray-300 shadow-md">
                Cancel
              </button>
            </>
          )}

          {status === 'loading' && <div className="py-4 font-bold text-[#1e8585]">Restoring data... Please wait.</div>}
          {status === 'success' && <div className="py-4 font-bold text-emerald-600">✓ Company restored successfully! Redirecting...</div>}

          {status === 'error' && (
            <div className="py-4 flex flex-col gap-4">
              <div className="text-red-600 font-bold">Error: {error}</div>
              <div className="flex gap-4 justify-center">
                <button onClick={() => setStatus('idle')} className="text-[#1e8585] underline font-bold">Try again</button>
                <button onClick={() => handleItemSelect({ name: 'Quit' })} className="bg-gray-200 px-8 py-2 font-bold hover:bg-gray-300 shadow-md">Cancel</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};